import { useImmerReducer } from "use-immer";
import { errorMessage } from "../../../utils/helper";
import { useContext, useEffect } from "react";
import {
  getPromosAndNotificationsApi,
  getPromosAndNotificationsCountApi,
} from "../api/promosAndNotificationsApi";
import { DEFAULT_PAGINATION } from "../../../utils";
import { AppContext, TableStateContext } from "../../../store";
import { useDropdownApis } from "../../../shared";

const tableName = "notification-and-promos";

const INITIAL_FILTER_STATE = {
  status: null,
  type: null,
  store: [],
};

const reducer = (draft, action) => {
  switch (action.type) {
    case "SET_TABLE_LOADING":
      draft.tableLoading = action.payload;
      break;
    case "SET_PROMOS_AND_NOTIFICATIONS":
      draft.pagination = action.payload.pagination;
      draft.promosAndNotifications = action.payload.list;
      break;
    case "SET_GOTO_PAGE":
      draft.pagination.pageIndex = action.payload;
      break;
    case "SET_PAGE_SIZE":
      draft.pagination.pageIndex = 1;
      draft.pagination.pageSize = action.payload;
      break;
    case "SET_SEARCH":
      draft.search = action.payload;
      draft.pagination.pageIndex = 1;
      break;
    case "SET_SORT_BY":
      draft.sortBy = action.payload;
      draft.pagination.pageIndex = 1;
      break;
    case "SET_FILTER":
      draft.filter[action.payload.name] = action.payload.value;
      draft.pagination.pageIndex = 1;
      if (!draft.isFilterApplied) draft.isFilterApplied = true;
      break;
    case "SET_APPLIED_FILTERS":
      draft.appliedFilters = action.payload;
      break;
    case "CLEAR_FILTERS":
      draft.filter = INITIAL_FILTER_STATE;
      draft.pagination.pageIndex = 1;
      draft.appliedFilters = [];
      break;
    case "SET_IS_FILTER_APPLIED":
      draft.isFilterApplied = action.payload;
      break;
    case "SET_TOTAL_COUNT":
      draft.totalCount = action.payload;
      break;
    default:
      break;
  }
};

export const usePromosAndNotificationsList = () => {
  const { filterState, filterStateDispatch } = useContext(TableStateContext);
  // Retrieve the filter applied for the specific table from the filter state context
  const storedFilter = filterState.params[tableName]?.filter;
  const storedAppliedFilter = filterState.params[tableName]?.appliedFilters;
  const storedPagination = filterState.params[tableName]?.pagination;
  const storedSortBy = filterState.params[tableName]?.sortBy;
  const storedSearch = filterState.params[tableName]?.search;

  const [state, dispatch] = useImmerReducer(reducer, {
    promosAndNotifications: [],
    pagination: storedPagination ?? DEFAULT_PAGINATION,
    tableLoading: false,
    search: storedSearch ?? "",
    sortBy: storedSortBy ?? [],
    filter: storedFilter ?? INITIAL_FILTER_STATE,
    appliedFilters: storedAppliedFilter ?? [],
    isFilterApplied: false,
    totalCount: {
      total_published_promos: 0,
      total_published_notifications: 0,
    },
  });

  const { appState } = useContext(AppContext);
  const { globalData } = appState;
  const { storeList } = useDropdownApis({ isStoreList: true });

  const breadcrumbLinks = [
    { name: "Dashboard", href: "/" },
    { name: "Promos & Notifications" },
  ];

  const data = [
    {
      id: 0,
      name: "Summer Sale",
      type: "Promo",
      stores: ["Store A", "Store B"],
      status: "Draft",
      actions: "",
    },
    {
      id: 1,
      name: "New Arrival Notification",
      type: "Promo",
      stores: ["Store C"],
      status: "Published",
      actions: "",
    },
    {
      id: 2,
      name: "Winter Clearance",
      type: "Promo",
      stores: ["Store B", "Store D"],
      status: "Unpublished",
    },
    {
      id: 3,
      name: "Holiday Discount",
      type: "Promo",
      stores: ["Store A"],
      status: "Ended",
      actions: "",
    },
    {
      id: 4,
      name: "Holiday Discount",
      type: "Notification",
      stores: ["Store A"],
      status: "Draft",
      actions: "",
    },
  ];

  const getPromosAndNotifications = async (
    params,
    sortBy,
    filter,
    appliedFilters,
  ) => {
    const fallbackMessage = "Failed to fetch promos and notifications.";
    dispatch({ type: "SET_TABLE_LOADING", payload: true });
    try {
      const response = await getPromosAndNotificationsApi(params);
      const { success, message, data } = response;
      if (success) {
        filterStateDispatch({
          type: "SET_DATA",
          page: tableName,
          data: {
            filter,
            appliedFilters,
            pagination: data.pagination,
            search: params?.search ?? "",
            sortBy: sortBy,
          },
        });
        dispatch({
          type: "SET_PROMOS_AND_NOTIFICATIONS",
          payload: {
            list: data.promo_notifications,
            pagination: data.pagination,
          },
        });
        dispatch({ type: "SET_IS_FILTER_APPLIED", payload: false });
      } else {
        errorMessage(message || fallbackMessage);
      }
    } catch (error) {
      const message =
        error.response?.data?.message || error?.message || fallbackMessage;
      errorMessage(message);
    } finally {
      dispatch({ type: "SET_TABLE_LOADING", payload: false });
    }
  };

  const getPromosAndNotificationsCount = async () => {
    try {
      const response = await getPromosAndNotificationsCountApi();
      const { success, data, message } = response;
      if (success) {
        dispatch({ type: "SET_TOTAL_COUNT", payload: data });
      } else {
        errorMessage(message);
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to fetch promos and notifications count";
      errorMessage(message);
    }
  };

  const getRequestUtilities = async (currentState) => {
    const { pagination } = currentState;
    const params = {
      page: pagination.pageIndex,
      per_page: pagination.pageSize,
    };

    if (currentState.search) {
      params.search = currentState.search;
    }

    if (currentState.sortBy.length > 0) {
      const sort = currentState.sortBy[0];
      params[`sort[${sort.id}]`] = sort.desc ? "desc" : "asc";
    }

    if (currentState.appliedFilters.length > 0) {
      currentState.appliedFilters.forEach((filter) => {
        if (filter.type === "store") {
          if (!params["store_id"]) params["store_id"] = [];

          params.store_id.push(filter.value);
        } else {
          params[filter.type] = filter.value;
        }
      });
    }

    return params;
  };

  const getAppliedFilters = (state) => {
    const appliedFilters = [];

    Object.entries(state).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        value.forEach((val) => {
          appliedFilters.push({
            type: key,
            label: val.name,
            value: val.id,
          });
        });
      } else if (key !== "store" && value) {
        appliedFilters.push({
          type: key,
          label: value.label,
          value: value.value,
        });
      }
    });

    dispatch({
      type: "SET_APPLIED_FILTERS",
      payload: appliedFilters,
    });
  };

  const gotoPage = async (page) => {
    dispatch("SET_GOTO_PAGE", page);

    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageIndex: page },
    });

    await getPromosAndNotifications(
      params,
      state.sortBy,
      state.filter,
      state.appliedFilters,
    );
  };

  const setPageSize = async (e) => {
    const newPageSize = +e.target.value;

    dispatch("SET_PAGE_SIZE", newPageSize);

    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageSize: newPageSize, pageIndex: 1 },
    });

    await getPromosAndNotifications(
      params,
      state.sortBy,
      state.filter,
      state.appliedFilters,
    );
  };

  const onTableSearch = async (searchText) => {
    dispatch({ type: "SET_SEARCH", payload: searchText });

    const params = await getRequestUtilities({
      ...state,
      search: searchText,
      pagination: { ...state.pagination, pageIndex: 1 },
    });

    getPromosAndNotifications(
      params,
      state.sortBy,
      state.filter,
      state.appliedFilters,
    );
  };

  const onSortChange = async (sortBy) => {
    dispatch({ type: "SET_SORT_BY", payload: sortBy });

    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageIndex: 1 },
      sortBy: sortBy,
    });

    getPromosAndNotifications(
      params,
      sortBy,
      state.filter,
      state.appliedFilters,
    );
  };

  const onFilterChange = async ({ name, value }) => {
    dispatch({ type: "SET_FILTER", payload: { name, value } });
  };

  const onApplyFilter = async () => {
    const params = await getRequestUtilities({
      ...state,
      pagination: { ...state.pagination, pageIndex: 1 },
    });

    getPromosAndNotifications(
      params,
      state.sortBy,
      state.filter,
      state.appliedFilters,
    );
  };

  const onClearAllFilter = async () => {
    dispatch({
      type: "CLEAR_FILTERS",
    });

    const params = await getRequestUtilities({
      ...state,
      filter: INITIAL_FILTER_STATE,
      appliedFilters: [],
      pagination: { ...state.pagination, pageIndex: 1 },
    });

    await getPromosAndNotifications(
      params,
      state.sortBy,
      INITIAL_FILTER_STATE,
      [],
    );
  };

  const onClearFilter = async (filterToRemove) => {
    onFilterChange({
      name: filterToRemove.type,
      value:
        filterToRemove.type === "store"
          ? state.filter.store.filter(
              (store) => store.id !== filterToRemove.value,
            )
          : null,
    });
  };

  useEffect(() => {
    getAppliedFilters(state.filter);
  }, [state.filter]);

  useEffect(() => {
    getPromosAndNotificationsCount();
    getRequestUtilities(state).then((params) => {
      getPromosAndNotifications(
        params,
        state.sortBy,
        state.filter,
        state.appliedFilters,
      );
    });
  }, []);

  return {
    statusOptions: globalData?.promos_notifications?.status,
    typeOptions: globalData?.promos_notifications?.type,
    storeOptions: storeList,
    state,
    data,
    breadcrumbLinks,
    setPageSize,
    gotoPage,
    onTableSearch,
    onSortChange,
    onFilterChange,
    onApplyFilter,
    onClearAllFilter,
    onClearFilter,
  };
};
